import { useEffect } from 'react';

import { throttle } from 'lodash';

import { useEvent } from 'common/components/hooks/useEvent/useEvent';
import { getLastActivityFromLocalStorage, setLastActivityToLocalStorage } from 'common/helpers/localStorageHelper';
import useLocalStorageBackedState from 'common/helpers/useLocalStorageBackedState';
import usePrevious from 'common/helpers/usePrevious';

export type UseUserActivityTrackerInput = {
  onActivity: () => void;
};

const USER_ACTIVITY_SAMPLE_INTERVAL = 15000;

export const TRACKED_USER_EVENTS = ['click', 'mousemove', 'touchstart', 'touchmove', 'scroll', 'keyup'];

export default function useUserActivityTracker({ onActivity }: UseUserActivityTrackerInput) {
  const [lastActivity, setLastActivity] = useLocalStorageBackedState({
    get: getLastActivityFromLocalStorage,
    set: setLastActivityToLocalStorage,
  });

  const updateActivityIfNewer = useEvent((newActivity: number) => {
    if (newActivity > lastActivity) {
      setLastActivity(newActivity);
    }
  });

  // Records every user activity to the local storage.
  useEffect(() => {
    const handleUserActivity = throttle(() => {
      updateActivityIfNewer(Date.now());
    }, USER_ACTIVITY_SAMPLE_INTERVAL);

    TRACKED_USER_EVENTS.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      TRACKED_USER_EVENTS.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });

      handleUserActivity.cancel();
    };
  }, [updateActivityIfNewer]);

  const previousLastActivity = usePrevious(lastActivity) ?? -1;
  useEffect(() => {
    if (lastActivity > previousLastActivity) {
      onActivity();
    }
  }, [onActivity, lastActivity, previousLastActivity]);
}
